import { css, mergeStyles, ScreenWidthMinMedium, Theme, ThemeContext } from '@fluentui/react';
import { Icon } from '@fluentui/react/lib/Icon';
import { Link as CustomLink } from '@fluentui/react/lib/Link';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { IAccountCardProps, IAccountCardState } from './AccountCard.types';
import { getTelemetry } from '../../Telemetry/Telemetry';

const cardStyles = require('@microsoft/portal-app/lib/styling/patterns/Card.scoped.scss');
const tileStyles = require('@microsoft/portal-app/lib/styling/patterns/Tile.scoped.scss');
const accountCardStyles = require('./AccountCard.scoped.scss');

/**
 * The Account Home page represents the splash screen for the MyAccount page that will
 * contain all the tiles and general information about the account.
 */
export class AccountCard extends React.Component<IAccountCardProps, IAccountCardState> {
  public render(): React.ReactNode {
    const {
      cardKey,
      title,
      description,
      iconName,
      primaryLinkText,
      secondaryLinkText,
      primaryLink,
      secondaryLink,
      isInternalLink,
    } = this.props;

    const primaryIconLink = (
      <>
        <p>{primaryLinkText}</p>
        <Icon iconName={'ChevronRightMed'} className={accountCardStyles.iconLink} />
      </>
    );

    const secondaryIconLink = (
      <>
        <p>{secondaryLinkText || ''}</p>
        <Icon iconName={'ChevronRightMed'} className={accountCardStyles.iconLink} />
      </>
    );

    const cardLinkClass = (theme: Theme | undefined) =>
      mergeStyles(
        accountCardStyles.link,
        theme && {
          selectors: {
            [`@media (max-width: ${ScreenWidthMinMedium}px)`]: {
              fontSize: theme.fonts.small.fontSize,
            },
          },
        },
      );

    return (
      <ThemeContext.Consumer>
        {(theme: Theme | undefined) => {
          return (
            <div
              className={css(
                'ms-AccountInfo',
                'ms-tileCard',
                cardStyles.card,
                cardStyles.cardColumn,
                accountCardStyles.card,
              )}
            >
              <div className={css('ms-tileCardHeader', accountCardStyles.header)}>
                <div
                  className={mergeStyles(
                    tileStyles.tileTitle,
                    accountCardStyles.title,
                    theme && {
                      fontSize: `${theme.fonts.xxLarge.fontSize} !important`,
                      selectors: {
                        [`@media (max-width: ${ScreenWidthMinMedium}px)`]: {
                          fontSize: `${theme.fonts.xLarge.fontSize} !important`,
                        },
                      },
                    },
                  )}
                >
                  {title}
                </div>
                <Icon iconName={iconName} className={accountCardStyles.icon} />
              </div>
              <div className={css('ms-tileCardDescription', accountCardStyles.cardSectionContent)}>
                <p className={accountCardStyles.description}>{description}</p>
                <div className={accountCardStyles.actionLinkContainer}>
                  <div
                    className={css('ms-titleCardAction', tileStyles.action, accountCardStyles.actionLink)}
                    onClick={this._addTelemetry(cardKey)}
                  >
                    {isInternalLink ? (
                      <Link to={primaryLink} className={cardLinkClass(theme)}>
                        {primaryIconLink}
                      </Link>
                    ) : (
                      <CustomLink href={primaryLink} className={cardLinkClass(theme)}>
                        {primaryIconLink}
                      </CustomLink>
                    )}
                  </div>
                  {secondaryLink && (
                    <div
                      className={css('ms-titleCardAction', tileStyles.action, accountCardStyles.actionLink)}
                      onClick={this._addTelemetry(cardKey)}
                    >
                      {isInternalLink ? (
                        <Link className={accountCardStyles.link} to={secondaryLink}>
                          {secondaryIconLink}
                        </Link>
                      ) : (
                        <CustomLink className={accountCardStyles.link} href={secondaryLink}>
                          {secondaryIconLink}
                        </CustomLink>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }}
      </ThemeContext.Consumer>
    );
  }

  private _addTelemetry = (title: string) => () => {
    const telemetry = getTelemetry();
    telemetry.reportCustomEvent('overview/account-card/clicked', {
      cardName: title,
    });
  };
}
